// FLAGS: consent
// The one signal Marketing labels a my-environment visit with: a single `login_success` push carrying the
// event name and nothing else. Kept out of auth.service.ts so the guard is unit-testable without
// rendering a hook, and so the session path calls one function that cannot throw.
import { hasConsentFor } from "@src/utils/cookiebot";

// FLAGS: consent
// A visit is one browser tab. The guard lives in `sessionStorage`, so a reload stays the same visit, a
// second tab counts as a second visit, and a logout followed by another login in the same tab does not
// push again. `localStorage` would never expire; a module-level flag would reset on reload and make every
// refresh double-count.
const VISIT_GUARD_KEY = "gtm_login_success_sent";

export const LOGIN_SUCCESS_EVENT = "login_success";

// FLAGS: consent
// `statistics` is the Cookiebot category GoogleConsentManager maps to `analytics_storage`, so gating on it
// here means this event and Google's own storage decision follow the same answer.
const CONSENT_CATEGORY = "statistics";

// Cookiebot fires `OnConsentReady` when a stored decision is restored and `OnAccept` when the banner is
// answered. `OnDecline` is deliberately not listened for: this module only ever acts on a grant.
const CONSENT_EVENTS = ["CookiebotOnAccept", "CookiebotOnConsentReady"];

// The GTM dataLayer, narrowed to the one capability this module needs. The library types
// `window.dataLayer` as `Object | undefined`, which has no `push`, so the shape is asserted here rather
// than reaching for `any` — `no-any` is an error-level rule in docs/frontend/rules.md.
type DataLayerPushable = {
  push: (event: Record<string, unknown>) => void;
};

// gtm-preconsent.ts assigns `window.dataLayer = window.dataLayer || []` at import time, and App.tsx
// imports it first, so in the app this is always an array by the time an effect runs. It still returns
// null defensively: a test, an extension or a future import reorder can leave it absent.
const getPushableDataLayer = (): DataLayerPushable | null => {
  const { dataLayer } = window;
  if (!dataLayer) {
    return null;
  }

  const candidate = dataLayer as DataLayerPushable;
  return typeof candidate.push === "function" ? candidate : null;
};

// FLAGS: silent-failure
// Deliberate: `sessionStorage` throws in Safari private mode and in some embedded webviews. Treating that
// as "already sent" fails closed — we lose a measurement rather than push on every render.
const alreadySentThisVisit = (): boolean => {
  try {
    return window.sessionStorage.getItem(VISIT_GUARD_KEY) === "true";
  } catch {
    return true;
  }
};

const markSentThisVisit = (): void => {
  try {
    window.sessionStorage.setItem(VISIT_GUARD_KEY, "true");
  } catch {
    // Nothing to do. The push has already happened; the worst case is one extra push in this tab.
  }
};

// FLAGS: silent-failure
// Deliberate and required by AC-7: measurement must never block authentication. This runs inside the
// session path, so a throw here would break login for every my-environment locale at once.
const pushLoginSuccess = (): void => {
  try {
    const dataLayer = getPushableDataLayer();
    if (!dataLayer) {
      return;
    }

    // FLAGS: personal-data
    // This object is the event name and nothing else — no user id, email, screen name or referrer. What
    // GA4 sends is wider than what is pushed here: the tag attaches `page_location`, `page_title` and
    // `page_referrer` to every event by default, and this fires on the first render of a restored
    // session, so the route a user deep-linked into travels with the hit. Narrowing that is a GA4 tag
    // setting (Fields to Set), not something this module can suppress — see OQ-2.
    //
    // The area label (`my_end_user`, `my_customer`, `utility`) is derived in GTM from the hostname, so it
    // is not sent from here either.
    dataLayer.push({ event: LOGIN_SUCCESS_EVENT });
  } catch {
    return;
  }

  markSentThisVisit();
};

// Holds the listener while it is armed, so it doubles as the "already attached" flag. Page-scoped, not
// visit-scoped: after a reload the listeners have to be attached again, and `sessionStorage` — not this
// reference — is what keeps the send itself once per visit.
let consentListener: (() => void) | null = null;

const detachConsentListeners = (): void => {
  const listener = consentListener;
  if (!listener) {
    return;
  }

  consentListener = null;
  CONSENT_EVENTS.forEach((eventName) => {
    window.removeEventListener(eventName, listener);
  });
};

// FLAGS: consent
// Re-checks rather than assuming: Cookiebot fires `OnAccept` for a partial acceptance too, so an accept
// that leaves `statistics` off must not push. A decline keeps the listener attached, because the user can
// reopen the banner and grant later in the same visit.
const handleConsentChange = (): void => {
  if (alreadySentThisVisit()) {
    detachConsentListeners();
    return;
  }

  if (!hasConsentFor(CONSENT_CATEGORY)) {
    return;
  }

  detachConsentListeners();
  pushLoginSuccess();
};

const attachConsentListeners = (): void => {
  if (consentListener) {
    return;
  }

  consentListener = handleConsentChange;
  CONSENT_EVENTS.forEach((eventName) => {
    window.addEventListener(eventName, handleConsentChange);
  });
};

// FLAGS: consent
// The gate AC-6 needs. `hasConsentFor` fails closed — no Cookiebot object on the page means `false` — and
// on a restored session the banner script is usually still loading when this first runs. Returning there
// would drop the event for the whole visit, because the effect in useAuthentication only re-runs when
// `isLoggedIn` changes. So a missing grant arms a listener instead of giving up, and the event fires the
// moment `statistics` is granted.
//
// Consent Mode is not a substitute for this. `analytics_storage: "denied"` limits what Google *stores*,
// not what the browser *sends*: a denied-consent hit still leaves carrying GA4's automatic
// `page_location`, which on a restored session is whatever route the user deep-linked into.
export const signalLoginSuccess = (): void => {
  if (typeof window === "undefined") {
    return;
  }

  if (alreadySentThisVisit()) {
    return;
  }

  if (!hasConsentFor(CONSENT_CATEGORY)) {
    attachConsentListeners();
    return;
  }

  pushLoginSuccess();
};

// Test seam only. Nothing in `src/` calls this; a visit boundary is a tab, not something the app resets.
export const resetLoginSuccessGuardForTests = (): void => {
  detachConsentListeners();

  try {
    window.sessionStorage.removeItem(VISIT_GUARD_KEY);
  } catch {
    // ignored
  }
};
